import { useEffect, useState } from "react"
import {
  Center,
  Heading,
  Spinner,
  useToast,
} from "@chakra-ui/react"
import { NavLayout } from "../components/NavLayout"
import useAxiosPrivate from "../hooks/useAxiosPrivate"
import { useNavigate, useParams } from "react-router-dom"
import { UserForm } from "../components/Forms/UserForm"
import { addServerErrors } from "../services/add-server-errors"

type FormData = {
  id: string
  username: string
  password: string
  confirm_password: string
  first_name: string
  last_name: string
  phone: string
  email_address: string
  organization: string
  timezone: string
  role: string
  active: string
}

type Errors = {
  [key: string]: { message: string }
}

const initialErrors: Errors = {}

const initialFormData: FormData = {
  id: "",
  username: "",
  password: "",
  confirm_password: "",
  first_name: "",
  last_name: "",
  phone: "",
  email_address: "",
  organization: "",
  timezone: "",
  role: "2",
  active: "1",
}

export const EditUser = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const toast = useToast()
  const { userId } = useParams()
  const [data, setData] = useState(initialFormData)
  const [errors, setErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const endpoint = `/users/edit/${userId}`

  useEffect(() => {
    setLoading(true)
    let isMounted = true
    const controller = new AbortController()

    const getData = async () => {
        try {
          const response = await axiosPrivate.get(endpoint, {
            signal: controller.signal
          })
          isMounted && setData(response.data.user)
          setLoading(false)
        } catch (err: any) {
          console.error(err)
          toast({
            title: 'Error',
            description: err.message,
            status: 'error',
            isClosable: true,
          })
          navigate(-1)
        }
    }

    const timeout = setTimeout(getData, 500)

    return () => {
        isMounted = false
        isMounted && controller.abort()
        clearTimeout(timeout)
    }
  }, [axiosPrivate, endpoint, navigate, toast])

  const handleSubmit = async (formData: FormData) => {
    setSubmitting(true)
    try {
      const response = await axiosPrivate.post(endpoint, formData)

      if (!response?.data?.user?.errors) {
        toast({
          title: 'Success',
          description: 'User successfully updated!',
          status: 'success',
          isClosable: true,
        })
        navigate(-1)
      } else {
        addServerErrors(response?.data?.user?.errors, setErrors)
        toast({
          title: 'Error',
          description: 'Please review any errors and resubmit',
          status: 'error',
          isClosable: true,
        })
      }
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e.message,
        status: 'error',
        isClosable: true,
      })
    }
    setSubmitting(false)
  }

  return (
    <NavLayout
      left={
        <Heading as="h1" size="lg">Edit User</Heading>
      }
    >
      {!loading
        ? (
          <UserForm edit={true} errors={errors} handleSubmit={handleSubmit} initialFormData={data} loading={submitting} />
        ) : (
          <Center>
            <Spinner
              thickness='4px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='xl'
            />
          </Center>
        )
      }
    </NavLayout>
  )
}
